<template>
    <section
        v-if="hasContent"
        class="slice slice-content-grid slice-content-grid--resources"
        aria-label="content-grid"
        data-testid="slice-content-grid-resources"
    >
        <div class="slice-content">
            <PrismicRichText
                v-if="isFilled.richText(slice.primary.title)"
                :field="slice.primary.title"
                class="slice-content-grid--resources__title mui-title"
                data-testid="slice-content-grid-resources-title"
            />

            <div class="slice-content-grid--resources__grid">
                <ContentGridResourcesCard
                    v-if="featuredItem"
                    :resource="featuredItem"
                    variant="featured"
                    data-testid="slice-content-grid-resources-featured"
                >
                    <template v-if="featuredItem.type === 'collection'" #collection-thumbnail-overlay>
                        <ContentGridResourcesCollectionThumbnail />
                    </template>
                </ContentGridResourcesCard>

                <div v-if="sideItems.length" class="slice-content-grid--resources__side" data-testid="slice-content-grid-resources-side">
                    <ContentGridResourcesCard
                        v-for="(item, index) in sideItems"
                        :key="item.id"
                        :resource="item"
                        variant="compact"
                        :data-testid="`slice-content-grid-resources-side-item--${index}`"
                    >
                        <template v-if="item.type === 'collection'" #collection-thumbnail-overlay>
                            <ContentGridResourcesCollectionThumbnail />
                        </template>
                    </ContentGridResourcesCard>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {ContentGridSlice, ContentGridSliceContentGridResourcesPrimaryItemsItem} from '@prismicio-types';
    import {useContentHelpers} from '../../../composables/useContentHelpers';
    import ContentGridResourcesCard from '../components/ContentGridResourcesCard.vue';
    import ContentGridResourcesCollectionThumbnail from '../components/ContentGridResourcesCollectionThumbnail.vue';
    import type {ContentGridResource} from '../contentGridResources.types';

    type ContentGridResourcesInZone = Extract<ContentGridSlice, {variation: 'contentGridResources'}>;

    const props = defineProps(getSliceComponentProps<ContentGridResourcesInZone>());

    const {isFilledContentRelationship} = useContentHelpers();

    function resolveResourcesFromGroupItem(item: ContentGridSliceContentGridResourcesPrimaryItemsItem): ContentGridResource[] {
        const resources: ContentGridResource[] = [];

        if (isFilledContentRelationship(item.article) && !item.article.isBroken && item.article.type === 'article') {
            resources.push(item.article);
        }
        if (isFilledContentRelationship(item.event) && !item.event.isBroken && item.event.type === 'event') {
            resources.push(item.event);
        }
        if (isFilledContentRelationship(item.guide) && !item.guide.isBroken && item.guide.type === 'guide') {
            resources.push(item.guide);
        }
        if (isFilledContentRelationship(item.successStory) && !item.successStory.isBroken && item.successStory.type === 'successStory') {
            resources.push(item.successStory);
        }
        if (isFilledContentRelationship(item.collection) && !item.collection.isBroken && item.collection.type === 'collection') {
            resources.push(item.collection);
        }

        return resources;
    }

    const resolvedItems = computed(() => props.slice.primary.items.flatMap(resolveResourcesFromGroupItem));

    const featuredItem = computed(() => resolvedItems.value[0] ?? null);
    const sideItems = computed(() => resolvedItems.value.slice(1, 4));
    const hasContent = computed(() => resolvedItems.value.length > 0);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-content-grid--resources {
        .slice-content {
            text-align: left;
        }

        &__title {
            margin-bottom: var(--joy-core-spacing-8);

            @include mq.screen_md(min) {
                margin-bottom: var(--joy-core-spacing-12);
            }
        }

        &__grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: var(--joy-core-spacing-6);

            @include mq.screen_sm(min) {
                grid-template-columns: 1fr 1fr;
                gap: var(--joy-core-spacing-8);
            }

            @include mq.screen_md(min) {
                gap: var(--joy-core-spacing-12);
            }
        }

        &__side {
            display: flex;
            flex-direction: column;
            border-top: 1px solid var(--joy-color-neutral-30);

            @include mq.screen_sm(min) {
                display: grid;
                grid-template-rows: repeat(3, minmax(190px, 1fr));
                gap: var(--joy-core-spacing-6);
                border-top: none;
            }

            .slice-content-grid--resources__card:first-child {
                padding-top: var(--joy-core-spacing-5);

                @include mq.screen_sm(min) {
                    padding-top: 0;
                }
            }

            .slice-content-grid--resources__card:not(:last-child) {
                padding-bottom: var(--joy-core-spacing-3);
                margin-bottom: var(--joy-core-spacing-3);
                border-bottom: 1px solid var(--joy-color-neutral-30);

                @include mq.screen_sm(min) {
                    padding-bottom: 0;
                    margin-bottom: 0;
                    border-bottom: none;
                }
            }
        }
    }
</style>
